import { IBlowDef, Segment } from './types';
import { seg } from './segments';
import { ParamReader } from './ParamReader';
import { sharedSpanListItems } from './spans';

const ATTACK_TYPES = ['physical', 'magical'];
const ELEMENTS = ['fire', 'ice', 'light', 'dark'];
const APPLY_TARGETS = ['self', 'enemy', 'ally', 'party', 'all'];
const SKILL_CHECK_TYPES = ['any attack', 'skill attacks only', 'normal attacks only'];
/** blow 253's own ApplyType enum — distinct order/labels from APPLY_TARGETS. */
const APPLY_TYPES_253 = ['self', 'target', 'all', 'enemy', 'friend'];

/** Parse `blowIndex|params` child group into a child segment. */
function childFromPipeGroup(group: string, durationMs: number): Segment | null {
    const bar = group.indexOf('|');
    const blowId = ParamReader.num(bar >= 0 ? group.slice(0, bar) : group);
    if (isNaN(blowId)) {
        return null;
    }
    return seg.child(blowId, bar >= 0 ? group.slice(bar + 1) : '', durationMs);
}

/**
 * Spans the trailing `[id;id;...]` skill-id list after the last `|` in `raw` (absolute offset `bar`
 * is the index of that `|` within `raw`). The whole ID region (first ID start → last ID end) gets
 * ONE shared span, attached to every skill ref, so the entire list occupies a single palette slot.
 * Falls back to unspanned skill refs if the tail isn't a single bracket group.
 */
function trailingSkillIdItems(raw: string, bar: number): Segment[][] {
    const tailReader = new ParamReader(raw.slice(bar + 1), bar + 1);
    const tg = tailReader.groupsWithSpans();
    const idsReader = (tg.groups.length === 1 && tg.rest === '')
        ? new ParamReader(tg.groups[0].text, tg.groups[0].start)
        : null;
    const tokens = idsReader
        ? idsReader.fields(';')
        : ParamReader.list(tailReader.raw.replace(/\[|\]/g, ''), ';');
    return sharedSpanListItems(tokens, idsReader, ';', seg.skill);
}

export const triggers: Record<number, IBlowDef> = {
    288: {
        name: 'Apply effect on successful hit of type',
        wikiDesc: 'When you successfully land an attack of the given attack type and element against ' +
            'the given target type, applies the listed state effect(s) at a chance.',
        describe({ params }) {
            const g = params.groupsWithSpans();
            if (g.groups.length < 2) {
                return null;
            }
            const settingsReader = new ParamReader(g.groups[0].text, g.groups[0].start);
            const s = settingsReader.fields();
            const attackType = ParamReader.num(s[0]);
            const element = ParamReader.num(s[1]);
            const target = ParamReader.num(s[2]);
            const chance = ParamReader.num(s[4]);
            const durationMs = ParamReader.num(s[5]) || 0;
            const maxCount = ParamReader.num(s[6]) || 0;
            const cooldownMs = ParamReader.num(s[7]) || 0;
            const skillCheck = ParamReader.num(s[8]);
            const requiredSes = s[9] ? ParamReader.numList(s[9]) : [];
            if (isNaN(attackType) || isNaN(element) || isNaN(target)) {
                return null;
            }
            const out: Segment[] = [
                'When landing a ', seg.en(attackType, ATTACK_TYPES, 'any', settingsReader.span(0) || undefined),
                ' ', seg.en(element, ELEMENTS, 'any', settingsReader.span(1) || undefined), ' hit',
            ];
            if (!isNaN(skillCheck)) {
                out.push(' with ', seg.en(
                    skillCheck, SKILL_CHECK_TYPES, 'unknown', settingsReader.span(8) || undefined));
            }
            if (requiredSes.length) {
                out.push(' while afflicted with ',
                    seg.list(requiredSes.map((id) => [seg.blow(id)] as Segment[])));
            }
            out.push(', at a ', seg.val(
                (isNaN(chance) ? 100 : chance) + '%', (!isNaN(chance) && settingsReader.span(4)) || undefined),
                ' chance apply to ', seg.en(target, APPLY_TARGETS, 'unknown', settingsReader.span(2) || undefined));
            if (maxCount > 0) {
                out.push(', max ', seg.num(maxCount, settingsReader.span(6) || undefined), ' times');
            }
            if (cooldownMs > 0) {
                out.push(', with a ', seg.secs(cooldownMs, settingsReader.span(7) || undefined), ' cooldown');
            }
            out.push(':');
            for (let i = 1; i < g.groups.length; ++i) {
                const child = childFromPipeGroup(g.groups[i].text, durationMs);
                if (child) {
                    out.push(child);
                }
            }
            return out;
        },
    },
    296: {
        name: 'Apply state effect upon other skill use',
        wikiDesc: 'Upon using the listed trigger skills, at a Rate% chance, applies the given state ' +
            'effect(s) for the given duration, with a cooldown (ms) between triggers.',
        note: 'All three settings fields (Rate;Duration;Cooldown) must be present.',
        describe({ params }) {
            const bar = params.raw.lastIndexOf('|');
            if (bar < 0) {
                return null;
            }
            const front = new ParamReader(params.raw.slice(0, bar)).groupsWithSpans();
            if (front.groups.length < 2) {
                return null;
            }
            const settingsReader = new ParamReader(front.groups[0].text, front.groups[0].start);
            const settings = settingsReader.numbers();
            const rate = settings[0];
            const durationMs = settings[1] || 0;
            const cooldownMs = settings[2] || 0;
            if (isNaN(rate)) {
                return null;
            }
            const children: Segment[] = [];
            for (let i = 1; i < front.groups.length; ++i) {
                const grp = front.groups[i].text;
                const slash = grp.indexOf('/');
                const blowId = ParamReader.num(slash >= 0 ? grp.slice(0, slash) : grp);
                if (!isNaN(blowId)) {
                    children.push(seg.child(blowId, slash >= 0 ? grp.slice(slash + 1) : '', durationMs));
                }
            }
            const items = trailingSkillIdItems(params.raw, bar);
            const out: Segment[] = [
                'Upon using ', seg.list(items),
                ', at a ', seg.val(rate + '%', settingsReader.span(0) || undefined), ' chance apply',
            ];
            if (durationMs > 0) {
                out.push(', lasting ', seg.secs(durationMs, settingsReader.span(1) || undefined));
            }
            if (cooldownMs > 0) {
                out.push(', with a ', seg.secs(cooldownMs, settingsReader.span(2) || undefined),
                    ' cooldown');
            }
            out.push(':');
            return out.concat(children);
        },
    },
    159: {
        name: 'Execute damage',
        wikiDesc: 'Deal bonus damage when the target is under ThresholdHP%. Skill damage is increased ' +
            'by BonusDamagePerPercentHPUnderThreshold% for every 1% the target is under ThresholdHP%.',
        describe({ params }) {
            const f = params.numbers();
            const threshold = f[0];
            const bonusPerPct = f[1];
            if (isNaN(threshold) || isNaN(bonusPerPct)) {
                return null;
            }
            return [
                'Upon striking a target under ', seg.pct(threshold), ' HP, deals ', seg.pct(bonusPerPct),
                ' additional skill damage per 1% missing HP below ', seg.pct(threshold),
            ];
        },
    },
    160: {
        name: 'Max Damage Add',
        wikiDesc: "Gives damage equal to the holder's max physical ATK at a certain chance when hit.",
        describe({ params }) {
            const chance = params.nextNum();
            if (isNaN(chance)) {
                return null;
            }
            return [
                'Upon being hit, at a ', seg.pct(chance),
                " chance, deals damage equal to the holder's max PATK",
            ];
        },
    },
    161: {
        name: 'Crash/Broken Bones',
        wikiDesc: 'Adds extra flat damage per hit for a certain amount of time.',
        describe({ params }) {
            const dmg = params.nextNum();
            if (isNaN(dmg)) {
                return null;
            }
            return ['Adds ', seg.num(dmg), ' extra flat damage per hit'];
        },
    },
    162: {
        name: 'Breaking Point',
        wikiDesc: "On an accurate hit, destroys a portion of the enemy's super armor and deals " +
            'critical damage. Additionally applies a configurable state effect on skill use (Breaking ' +
            'Point commonly uses blow 16, HP Restore%).',
        describe({ params, durationMs }) {
            const f = params.fields();
            const rate = ParamReader.num(f[0]);
            if (isNaN(rate)) {
                return null;
            }
            const out: Segment[] = [
                'On an accurate hit, at a ', seg.pct(rate),
                " chance, destroys a portion of the enemy's super armor and deals critical damage",
            ];
            if (f.length > 1 && f[1]) {
                const parts = f[1].split('/');
                const blowId = ParamReader.num(parts[0]);
                if (!isNaN(blowId)) {
                    const childParams = parts.length > 1 ? parts[1] : '';
                    out.push('. On skill use, applies: ', seg.child(blowId, childParams, durationMs));
                }
            }
            return out;
        },
    },
    166: {
        name: 'Increased damage against afflicted targets',
        wikiDesc: 'Attacks against targets afflicted with EffectId take Bonus% more (+) or less (-) ' +
            'damage.',
        describe({ params }) {
            const f = params.numbers();
            const effectId = f[0];
            const bonus = f[1];
            if (isNaN(effectId) || isNaN(bonus)) {
                return null;
            }
            const dir = bonus < 0 ? 'less' : 'more';
            return [
                'Attacks against targets afflicted with ', seg.blow(effectId), ' deal ',
                seg.pct(Math.abs(bonus)), ` ${dir} damage`,
            ];
        },
    },
    168: {
        name: 'Shock Transition',
        wikiDesc: 'When a light-element hit strikes an electrocuted (blow 43) target, Prob chance to ' +
            'fire a shock projectile (from the Skill_ShockInfection action) at the nearest ' +
            'non-electrocuted enemy within Radius, dealing HitPercent-scaled damage off the base ' +
            'attack. Internal ~1s cooldown.',
        describe({ params }) {
            const f = params.numbers();
            const prob = f[0];
            const dmgType = f[1];
            const radius = f[2];
            const hitPct = f[3];
            if (isNaN(prob) || isNaN(dmgType) || isNaN(radius) || isNaN(hitPct)) {
                return null;
            }
            const out: Segment[] = [
                'On a light hit against a target afflicted with ', seg.blow(43), ', at a ', seg.pct(prob),
                ' chance, fires a shock projectile at the nearest non-electrocuted enemy within ',
                seg.num(radius), ' range, dealing ', seg.pct(hitPct), ' of base ',
                seg.en(dmgType, ['physical', 'magical'], 'unknown'), ' attack as damage',
            ];
            if (f[4] === 1) {
                out.push(' (hits the target itself if nobody else is in range)');
            }
            return out;
        },
    },
    169: {
        name: 'Critical Break',
        wikiDesc: 'When own attack critically hits an enemy, deals additional damage at a certain ' +
            'probability.',
        describe({ params }) {
            const f = params.numbers();
            const rate = f[0];
            const bonus = f[1];
            if (isNaN(rate) || isNaN(bonus)) {
                return null;
            }
            const dir = bonus < 0 ? 'less' : 'more';
            return [
                'On a critical hit, at a ', seg.pct(rate), ' chance, deals ', seg.pct(Math.abs(bonus)),
                ` ${dir} damage`,
            ];
        },
    },
    172: {
        name: 'Exploit',
        wikiDesc: 'When a fire/ice/electric/poison attack accurately hits an enemy afflicted with the ' +
            'matching status (burn/freeze/electrocute/poison), deals additional damage at a probability.',
        note: '3rd field is undocumented; functions as a consume-the-affliction flag.',
        describe({ raw }) {
            let body = raw.trim();
            if (body.charAt(0) === '[' && body.charAt(body.length - 1) === ']') {
                body = body.slice(1, -1);
            }
            const f = new ParamReader(body).fields();
            const rate = ParamReader.num(f[0]);
            const requiredBlowId = ParamReader.num(f[1]);
            const bonus = ParamReader.num(f[3]);
            if (isNaN(rate) || isNaN(requiredBlowId) || isNaN(bonus)) {
                return null;
            }
            const out: Segment[] = [
                'When hitting a target afflicted with ', seg.blow(requiredBlowId), ', at a ', seg.pct(rate),
                ' chance, deals ', seg.pct(bonus), ' additional damage',
            ];
            if (ParamReader.num(f[2]) === 1) {
                out.push(', consuming the affliction');
            }
            return out;
        },
    },
    178: {
        name: 'Apply Effect When Hit',
        wikiDesc: "Every time the holder gets hit, activates the given state effect with the given " +
            'parameters.',
        describe({ params }) {
            const g = params.groups();
            if (g.groups.length < 2) {
                return null;
            }
            const settings = new ParamReader(g.groups[0]).fields();
            const probability = ParamReader.num(settings[4]);
            const durationMs = ParamReader.num(settings[5]) || 0;
            const maxTimes = ParamReader.num(settings[6]) || 0;
            const cooldownMs = ParamReader.num(settings[7]) || 0;
            const grp = g.groups[1];
            const slash = grp.indexOf('/');
            const blowId = ParamReader.num(slash >= 0 ? grp.slice(0, slash) : grp);
            if (isNaN(probability) || isNaN(blowId)) {
                return null;
            }
            const child = seg.child(blowId, slash >= 0 ? grp.slice(slash + 1) : '', durationMs);
            const out: Segment[] = [
                'Whenever hit, at a ', seg.pct(probability), ' chance, apply',
            ];
            if (maxTimes > 0) {
                out.push(' (max ', seg.num(maxTimes), ' times');
                if (cooldownMs > 0) {
                    out.push(', ', seg.secs(cooldownMs), ' cooldown');
                }
                out.push(')');
            } else if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            out.push(': ', child);
            return out;
        },
    },
    229: {
        name: 'Apply State Blow on Time',
        wikiDesc: "Every Interval ms, Chance to apply SkillID's state effects to the holder. All 4 " +
            'parameters are required; SkillLevel is ignored — the skill is always created at level 1.',
        describe({ params }) {
            const f = params.numbers();
            const intervalMs = f[0];
            const chance = f[1];
            const skillId = f[2];
            const skillLevel = f[3];
            if (isNaN(intervalMs) || isNaN(chance) || isNaN(skillId) || isNaN(skillLevel)) {
                return null;
            }
            return [
                'Every ', seg.secs(intervalMs), ', at a ', seg.pct(chance),
                ' chance, applies the state effects of ', seg.skill(skillId),
                ' (created at level 1, regardless of the given SkillLevel)',
            ];
        },
    },
    242: {
        name: 'Hit Damage Multiplier',
        wikiDesc: 'After hitting enough times, adjusts subsequent hits (from the same skill) by ' +
            'multiplier (1.0 = no change).',
        describe({ params }) {
            const f = params.numbers();
            const hitAmount = f[0];
            const multiplier = f[1];
            if (isNaN(hitAmount) || isNaN(multiplier)) {
                return null;
            }
            return [
                'After ', seg.num(hitAmount), ' hits from the same skill, multiplies subsequent hits by ',
                seg.pct(multiplier),
            ];
        },
    },
    253: {
        name: 'Additional State Info',
        wikiDesc: "While on the caster: any skill they use that contains state effect CondSEIndex " +
            "additionally applies TargetSEIndex with Value to ApplyType, inheriting the condition " +
            "SE's duration. Can multi-instance.",
        describe({ params, durationMs }) {
            const g = params.groups();
            if (g.groups.length < 2) {
                return null;
            }
            const s = new ParamReader(g.groups[0]).numbers();
            const condSeIndex = s[0];
            const targetSeIndex = s[1];
            const applyType = s[2];
            if (isNaN(condSeIndex) || isNaN(targetSeIndex) || isNaN(applyType)) {
                return null;
            }
            const value = g.groups[1];
            return [
                'Whenever a skill applies ', seg.blow(condSeIndex), ', additionally applies to ',
                seg.en(applyType, APPLY_TYPES_253, 'unknown'), ': ',
                seg.child(targetSeIndex, value, durationMs),
            ];
        },
    },
    271: {
        name: 'Basic State Base Add State',
        wikiDesc: 'Appears to apply a list of state effects (only works for basic stat state effects?).',
        note: 'Only confirmed to work for basic stat-type state effects.',
        describe({ params, durationMs }) {
            const f = params.fields();
            if (f.length < 2) {
                return null;
            }
            const children: Segment[] = [];
            for (let i = 0; i + 1 < f.length; i += 2) {
                const id = ParamReader.num(f[i]);
                if (isNaN(id)) {
                    continue;
                }
                children.push(seg.child(id, f[i + 1], durationMs));
            }
            if (!children.length) {
                return null;
            }
            const out: Segment[] = ['Applies: '];
            children.forEach((c, i) => {
                if (i > 0) {
                    out.push(', ');
                }
                out.push(c);
            });
            return out;
        },
    },
    276: {
        name: 'Apply other state effect upon skill use',
        wikiDesc: 'Apply StateEffectID with Parameters to the caster whenever they use SkillID1, ' +
            'SkillID2, ...',
        describe({ raw, durationMs }) {
            const underscore = raw.indexOf('_');
            if (underscore < 0) {
                return null;
            }
            const stateInfo = raw.slice(0, underscore);
            const skillListStr = raw.slice(underscore + 1);
            const slash = stateInfo.indexOf('/');
            const blowId = ParamReader.num(slash >= 0 ? stateInfo.slice(0, slash) : stateInfo);
            if (isNaN(blowId)) {
                return null;
            }
            const blowParam = slash >= 0 ? stateInfo.slice(slash + 1) : '';
            const skillIds = ParamReader.numList(skillListStr, ',');
            if (!skillIds.length) {
                return null;
            }
            return [
                'Upon using ', seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])),
                ', applies: ', seg.child(blowId, blowParam, durationMs),
            ];
        },
    },
    278: {
        name: 'Party execute damage',
        wikiDesc: 'Deal bonus damage when the target is above/below ThresholdHP%. Skill damage is ' +
            'increased by BonusDamagePerPercentHPAboveThreshold% for every 1% above/below ThresholdHP%.',
        describe({ params }) {
            const f = params.numbers();
            const threshold = f[0];
            const bonusPerPct = f[1];
            if (isNaN(threshold) || isNaN(bonusPerPct)) {
                return null;
            }
            const reverse = f[2] === 1;
            const dir = reverse ? 'above' : 'below';
            return [
                'Upon striking a target ', dir, ' ', seg.pct(threshold), ' HP, deals ',
                seg.pct(bonusPerPct), ' additional skill damage per 1% ', dir, ' ', seg.pct(threshold),
            ];
        },
    },
    280: {
        name: 'Dragon Shield',
        wikiDesc: "When hit and off cooldown (and at/below HPPercent if set), Probability% chance to " +
            "apply SubSkillID's effects to self and show EffectName (via state effect 100). " +
            "IgnoreDamage 1 fully negates the triggering hit's damage. Can multi-instance.",
        describe({ params }) {
            const f = params.fields();
            const subSkillId = ParamReader.num(f[0]);
            const cooldownSec = ParamReader.num(f[1]);
            const probability = ParamReader.num(f[2]);
            const effectName = f[3];
            if (isNaN(subSkillId) || isNaN(cooldownSec) || isNaN(probability) || !effectName) {
                return null;
            }
            const out: Segment[] = ['When hit'];
            if (cooldownSec > 0) {
                out.push(' (', seg.secs(cooldownSec * 1000), ' cooldown)');
            }
            const hpPercent = ParamReader.num(f[4]);
            if (!isNaN(hpPercent) && hpPercent > 0) {
                out.push(', at or below ', seg.pct(hpPercent), ' HP');
            }
            out.push(
                ', at a ', seg.pct(probability / 100), ' chance, applies ', seg.skill(subSkillId),
                "'s effects to self, showing ", seg.action(effectName),
            );
            if (f[5] === '1') {
                out.push(" (fully negates the triggering hit's damage)");
            }
            out.push(' (can multi-instance)');
            return out;
        },
    },
    281: {
        name: 'HP Below Use Skill (Feint)',
        wikiDesc: 'Assassin Feint (On Death Do Action?): below PercentOrAbsHP HP, applies one or ' +
            'more state effects (StateEffectID/Dura/Param). State effect 16 recovers HP, 99 grants ' +
            'invincibility.',
        note: 'Sub-effects are pipe-separated.',
        describe({ raw }) {
            const f = raw.split(';').map((s) => s.trim());
            if (f.length < 3) {
                return null;
            }
            const hpType = ParamReader.num(f[0]);
            const percentOrAbs = ParamReader.num(f[1]);
            if (isNaN(hpType) || isNaN(percentOrAbs)) {
                return null;
            }
            const subSpecs = f[2].split('|').map((s) => s.trim()).filter((s) => s !== '');
            const children: Segment[] = [];
            for (const spec of subSpecs) {
                const parts = spec.split('/');
                const blowId = ParamReader.num(parts[0]);
                if (isNaN(blowId)) {
                    continue;
                }
                const childDurationMs = ParamReader.num(parts[1]);
                const param = parts.length > 2 ? parts[2] : '';
                children.push(seg.child(blowId, param, isNaN(childDurationMs) ? 0 : childDurationMs));
            }
            if (!children.length) {
                return null;
            }
            const out: Segment[] = [
                'Below ', hpType === 1 ? seg.pct(percentOrAbs) : seg.num(percentOrAbs),
                hpType === 1 ? ' HP, applies: ' : ' HP (absolute), applies: ',
            ];
            children.forEach((c, i) => {
                if (i > 0) {
                    out.push(', ');
                }
                out.push(c);
            });
            if (f[3] === '1') {
                out.push(' (saved — one-time)');
            }
            if (f[4]) {
                out.push(', via ', seg.action(f[4]));
            }
            return out;
        },
    },
    283: {
        name: 'Sub Skill on Target Hit',
        wikiDesc: "On every successful hit, applies SubSkillID's effects (at the parent skill's " +
            'level) to TargetType. Range 0 = the hit target only, > 0 = every matching actor within ' +
            'Range of you. SkillOnly 1 = only skill/skill-projectile hits count. All 4 params required.',
        describe({ params }) {
            const f = params.numbers();
            const range = f[0];
            const subSkillId = f[1];
            const skillOnly = f[2];
            const targetType = f[3];
            if (isNaN(range) || isNaN(subSkillId) || isNaN(skillOnly) || isNaN(targetType)) {
                return null;
            }
            const out: Segment[] = [
                'On every successful hit', skillOnly === 1 ? ' from a skill' : '',
                ', applies ', seg.skill(subSkillId, params.span(1) || undefined), "'s effects to ",
                seg.en(targetType, APPLY_TARGETS, 'unknown', params.span(3) || undefined),
            ];
            if (range > 0) {
                out.push(' within ', seg.num(range, params.span(0) || undefined), ' range');
            } else {
                out.push(' (hit target only)');
            }
            return out;
        },
    },
    284: {
        name: 'Time Goes Growing',
        wikiDesc: "Every Interval ms spent without landing a hit, applies SubSkillID's effects to " +
            'self (stacking, duration refreshed); landing any hit resets the timer.',
        describe({ params }) {
            const f = params.numbers();
            const intervalMs = f[0];
            const subSkillId = f[1];
            if (isNaN(intervalMs) || isNaN(subSkillId)) {
                return null;
            }
            return [
                'Every ', seg.secs(intervalMs), ' spent without landing a hit, applies ',
                seg.skill(subSkillId),
                "'s effects to self (stacking, duration refreshed; landing a hit resets the timer)",
            ];
        },
    },
    285: {
        name: 'Do When Skill Begin',
        wikiDesc: 'Accumulates Value per stack (re-applications add their Value); when the holder ' +
            'begins any skill, fires SEIndex with the accumulated value for Duration, then consumes ' +
            'itself.',
        note: 'Pairs with 284 as "stack, then release on next skill".',
        describe({ params }) {
            const f = params.numbers();
            const seIndex = f[0];
            const durationMs = f[1];
            const valuePerStack = f[2];
            if (isNaN(seIndex) || isNaN(durationMs) || isNaN(valuePerStack)) {
                return null;
            }
            return [
                'Accumulates ', seg.num(valuePerStack),
                ' per stack; when the holder begins any skill, fires ', seg.blow(seIndex),
                ' with the accumulated value for ', seg.secs(durationMs), ', then consumes itself',
            ];
        },
    },
    286: {
        name: 'Check Immune Be Damage',
        wikiDesc: 'Deals more damage to enemies that resist StateEffectID, by Mod%. Used by Rampage ' +
            'Claw/Umbra.',
        note: 'Second field may route through blow 177; only the trailing Mod% is used.',
        describe({ params }) {
            const f = params.fields();
            if (f.length < 2) {
                return null;
            }
            const stateEffectId = ParamReader.num(f[0]);
            if (isNaN(stateEffectId)) {
                return null;
            }
            const second = f[1] || '';
            const slash = second.lastIndexOf('/');
            const modText = slash >= 0 ? second.slice(slash + 1) : second;
            const mod = ParamReader.num(modText);
            if (isNaN(mod)) {
                return null;
            }
            return [
                'Deals ', seg.pct(mod), ' more damage against enemies that resist ',
                seg.blow(stateEffectId),
            ];
        },
    },
    289: {
        name: 'Add State on Skill Begin',
        wikiDesc: "Used for Adept's Elemental Flair. Upon beginning SkillID, at a Probability% " +
            'chance, applies StateEffectID with Parameter to TargetType within EffectDistance, for ' +
            'Duration, up to MaxApplyCount times, with a CoolTimeMS cooldown.',
        note: 'TargetType matches 283/288 (self/enemy/ally/party/all).',
        describe({ params }) {
            const g = params.groups();
            if (g.groups.length < 2) {
                return null;
            }
            const s = new ParamReader(g.groups[0]).numbers();
            const element = s[0];
            const targetType = s[1];
            const distance = s[2];
            const probability = s[3];
            const applyDurationMs = s[4] || 0;
            const maxApplyCount = s[5] || 0;
            const cooldownMs = s[6] || 0;
            if (isNaN(element) || isNaN(targetType) || isNaN(probability)) {
                return null;
            }
            const childFields = new ParamReader(g.groups[1]).fields();
            const spec = childFields[0] || '';
            const slash = spec.indexOf('/');
            const blowId = ParamReader.num(slash >= 0 ? spec.slice(0, slash) : spec);
            if (isNaN(blowId)) {
                return null;
            }
            const blowParam = slash >= 0 ? spec.slice(slash + 1) : '';
            const skillId = ParamReader.num(childFields[2]);
            const out: Segment[] = [
                'Upon beginning ', isNaN(skillId) ? 'a skill' : seg.skill(skillId), ', at a ',
                seg.pct(probability / 100), ' chance, applies to ',
                seg.en(targetType, APPLY_TARGETS, 'unknown'),
            ];
            if (element >= 0) {
                out.push(' (', seg.en(element, ELEMENTS, 'unknown'), ' element)');
            }
            if (distance > 0) {
                out.push(' within ', seg.num(distance), ' range');
            }
            out.push(': ', seg.child(blowId, blowParam, applyDurationMs));
            if (maxApplyCount > 0) {
                out.push(', max ', seg.num(maxApplyCount), ' times');
            }
            if (cooldownMs > 0) {
                out.push(', ', seg.secs(cooldownMs), ' cooldown');
            }
            return out;
        },
    },
    290: {
        name: 'Add State on Summon Monster',
        wikiDesc: 'When you summon a listed monster, Probability% chance to apply a state effect to ' +
            'yourself (TargetSummon 0) or the summon (1; also refills its HP/MP). Ends after ' +
            'MaxApplyCount procs.',
        note: 'The second effect bracket is bugged (params get crossed); only the first is used.',
        describe({ params, durationMs }) {
            const g = params.groups();
            if (g.groups.length < 3) {
                return null;
            }
            const monsterIds = ParamReader.numList(g.groups[0], ';');
            const s = new ParamReader(g.groups[1]).fields();
            if (s.length < 5) {
                return null;
            }
            const targetSummon = ParamReader.num(s[0]);
            const probability = ParamReader.num(s[1]);
            const applyDurationMs = ParamReader.num(s[2]) || 0;
            const maxApplyCount = ParamReader.num(s[3]) || 0;
            const cooldownMs = ParamReader.num(s[4]) || 0;
            if (!monsterIds.length || isNaN(targetSummon) || isNaN(probability)) {
                return null;
            }
            const spec = g.groups[2] || '';
            const slash = spec.indexOf('/');
            const blowId = ParamReader.num(slash >= 0 ? spec.slice(0, slash) : spec);
            if (isNaN(blowId)) {
                return null;
            }
            const blowParam = slash >= 0 ? spec.slice(slash + 1) : '';
            const out: Segment[] = [
                'When summoning ', seg.list(monsterIds.map((id) => [seg.monster(id)] as Segment[])),
                ', at a ', seg.pct(probability / 100), ' chance, applies to ',
                targetSummon === 1 ? 'the summon (also refills its HP/MP)' : 'yourself', ': ',
                seg.child(blowId, blowParam, applyDurationMs || durationMs),
            ];
            if (maxApplyCount > 0) {
                out.push('; ends after ', seg.num(maxApplyCount), ' procs');
            }
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    295: {
        name: 'Add State on Break Super Armor',
        wikiDesc: 'When a received hit staggers you (your super armor breaks), applies a state ' +
            'effect to yourself; passive parent skills also trigger their cooldown.',
        note: "The second effect bracket is bugged like blow 290's; only the first is used.",
        describe({ params }) {
            const g = params.groups();
            if (g.groups.length < 2) {
                return null;
            }
            const s = new ParamReader(g.groups[0]).numbers();
            const applyDurationMs = s[0];
            const cooldownMs = s[1] || 0;
            if (isNaN(applyDurationMs)) {
                return null;
            }
            const spec = g.groups[1] || '';
            const slash = spec.indexOf('/');
            const blowId = ParamReader.num(slash >= 0 ? spec.slice(0, slash) : spec);
            if (isNaN(blowId)) {
                return null;
            }
            const blowParam = slash >= 0 ? spec.slice(slash + 1) : '';
            const out: Segment[] = [
                'When a received hit breaks your super armor, applies: ',
                seg.child(blowId, blowParam, applyDurationMs),
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown; passive parent skills also trigger it)');
            }
            return out;
        },
    },
    297: {
        name: 'Apply state effect on successful hit from skill',
        wikiDesc: 'Upon successful hit from TriggerSkillID1, at a Rate% chance for Duration ' +
            'milliseconds, apply StateEffectID with Parameters.',
        describe({ params }) {
            const bar = params.raw.lastIndexOf('|');
            if (bar < 0) {
                return null;
            }
            const front = new ParamReader(params.raw.slice(0, bar)).groupsWithSpans();
            if (front.groups.length < 2) {
                return null;
            }
            const settingsReader = new ParamReader(front.groups[0].text, front.groups[0].start);
            const settings = settingsReader.numbers();
            const rate = settings[0];
            const durationMs = settings[1] || 0;
            const cooldownMs = settings[2] || 0;
            const limit = settings[3] || 0;
            const targetType = settings[4];
            if (isNaN(rate)) {
                return null;
            }
            const children: Segment[] = [];
            for (let i = 1; i < front.groups.length; ++i) {
                const grp = front.groups[i].text;
                const slash = grp.indexOf('/');
                const blowId = ParamReader.num(slash >= 0 ? grp.slice(0, slash) : grp);
                if (!isNaN(blowId)) {
                    children.push(seg.child(blowId, slash >= 0 ? grp.slice(slash + 1) : '', durationMs));
                }
            }
            if (!children.length) {
                return null;
            }
            const items = trailingSkillIdItems(params.raw, bar);
            const out: Segment[] = [
                'Upon successfully hitting with ',
                seg.list(items),
                ', at a ', seg.val(rate + '%', settingsReader.span(0) || undefined), ' chance apply',
            ];
            out.push(':');
            children.forEach((c) => out.push(c));
            if (!isNaN(targetType)) {
                out.push(' (target type ', seg.id(targetType, settingsReader.span(4) || undefined), ')');
            }
            if (limit > 0) {
                out.push(', max ', seg.num(limit, settingsReader.span(3) || undefined), ' times');
            }
            if (cooldownMs > 0) {
                out.push(', ', seg.secs(cooldownMs, settingsReader.span(2) || undefined), ' cooldown');
            }
            return out;
        },
    },
    300: {
        name: 'Add Skill on Block/Parry',
        wikiDesc: 'Applies a state effect upon successfully blocking or parrying an attack.',
        describe({ params }) {
            const g = params.groups();
            if (g.groups.length < 2) {
                return null;
            }
            const s = new ParamReader(g.groups[0]).numbers();
            const applyDurationMs = s[0];
            const cooldownMs = s[1] || 0;
            if (isNaN(applyDurationMs)) {
                return null;
            }
            const skillIds = ParamReader.numList(g.groups[1], ';');
            if (!skillIds.length) {
                return null;
            }
            const out: Segment[] = [
                'Upon successfully blocking or parrying, applies ',
                seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])),
                "'s effects for ", seg.secs(applyDurationMs),
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    309: {
        name: 'Attack Increase on Successful Attack',
        wikiDesc: 'After a successful attack, own attack power increases by Mod.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return ['After a successful attack, own attack power increases by ', seg.num(mod)];
        },
    },
    316: {
        name: 'Appoint Target / Mark',
        wikiDesc: 'Marks the target actor for state effect 317.',
        describe() {
            return ['Marks the target actor for ', seg.blow(317)];
        },
    },
    317: {
        name: 'Attack Target w/ Mark',
        wikiDesc: 'Increases the listed summons’ aggro towards actors marked by state effect 316 ' +
            'by aggroAmount. (Illusory Chakra/Summon Buster/Chemical Grenade and Sin Summons have this ' +
            'effect.)',
        describe({ params }) {
            const g = params.groups();
            if (g.groups.length < 2) {
                return null;
            }
            const monsterIds = ParamReader.numList(g.groups[0], ';');
            const aggro = ParamReader.num(g.groups[1]);
            if (!monsterIds.length || isNaN(aggro)) {
                return null;
            }
            return [
                'Increases ', seg.list(monsterIds.map((id) => [seg.monster(id)] as Segment[])),
                "'s aggro towards actors marked by ", seg.blow(316), ' by ', seg.num(aggro),
            ];
        },
    },
    318: {
        name: 'Skill Hit Signal Filter',
        wikiDesc: "On the attacker: SkillID's hit signals only connect on targets whose HP%/SP% passes " +
            'the comparison; targets with ExcludeBlow applied (and NPCs) are always excluded.',
        describe({ params }) {
            const f = params.fields();
            const skillId = ParamReader.num(f[0]);
            const percent = ParamReader.num(f[1]);
            const statType = ParamReader.num(f[2]);
            const compare = ParamReader.num(f[3]);
            const excludeBlowId = ParamReader.num(f[4]);
            if (isNaN(skillId) || isNaN(percent) || isNaN(statType) || isNaN(compare) ||
                isNaN(excludeBlowId)) {
                return null;
            }
            const cmpSymbol = compare === 1 ? '≥' : '≤';
            return [
                'On the attacker, ', seg.skill(skillId), "'s hit signals only connect on targets whose ",
                seg.en(statType, ['HP', 'SP'], 'unknown'), '% ', cmpSymbol, ' ', seg.pct(percent / 100),
                ' (excludes targets with ', seg.blow(excludeBlowId), ' applied, and NPCs)',
            ];
        },
    },
    320: {
        name: 'Add State Effect After Duplicate',
        wikiDesc: "Every Tick ms applies StackSkillID's effects, stacking them; when the stacks reach " +
            "StackSkillID's max duplicate count, removes them and fires ApplySkillID's effects (petrify " +
            'tick-stacking). Will not begin if the same parent skill is already applied.',
        note: "StackSkillID's `_DuplicateCount` is the stack threshold; skills are created at level 1.",
        describe({ params }) {
            const f = params.fields();
            const tickMs = ParamReader.num(f[0]);
            const stackSkillId = ParamReader.num(f[1]);
            if (isNaN(tickMs) || isNaN(stackSkillId)) {
                return null;
            }
            const out: Segment[] = [
                'Every ', seg.secs(tickMs), ', applies the stacking effects of ', seg.skill(stackSkillId),
                ' (created at level 1; will not begin if the same parent skill is already applied)',
            ];
            const applySkillId = f.length > 2 ? ParamReader.num(f[2]) : NaN;
            if (!isNaN(applySkillId)) {
                out.push(
                    '. Upon reaching the max duplicate count, removes the stacks and fires ',
                    seg.skill(applySkillId), "'s effects",
                );
            }
            return out;
        },
    },
    322: {
        name: 'Use Skill on Successful Attack',
        wikiDesc: 'After a successful attack, uses SkillID at a % Chance.',
        describe({ params }) {
            const f = params.numbers();
            const skillId = f[0];
            const cooldownMs = f[1];
            const chance = f[2];
            if (isNaN(skillId) || isNaN(cooldownMs) || isNaN(chance)) {
                return null;
            }
            const out: Segment[] = [
                'After a successful attack, at a ', seg.pct(chance / 100), ' chance, uses ',
                seg.skill(skillId),
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    323: {
        name: 'When hit use skill',
        wikiDesc: 'When hit, uses SkillID at a % Chance.',
        describe({ params }) {
            const f = params.numbers();
            const skillId = f[0];
            const cooldownMs = f[1];
            const chance = f[2];
            if (isNaN(skillId) || isNaN(cooldownMs) || isNaN(chance)) {
                return null;
            }
            const out: Segment[] = [
                'When hit, at a ', seg.pct(chance / 100), ' chance, uses ', seg.skill(skillId),
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    327: {
        name: 'Use Skill on Damage Taken',
        wikiDesc: 'If you receive damage equal to at least Mod% of your HP, uses SkillID at a % Chance.',
        describe({ params }) {
            const f = params.numbers();
            const skillId = f[0];
            const cooldownMs = f[1];
            const rate = f[2];
            const minHpPct = f[3];
            if (isNaN(skillId) || isNaN(cooldownMs) || isNaN(rate) || isNaN(minHpPct)) {
                return null;
            }
            return [
                'When receiving damage equal to at least ', seg.pct(minHpPct / 100), ' of your HP, at a ',
                seg.pct(rate / 100), ' chance, uses ', seg.skill(skillId), ' (', seg.secs(cooldownMs),
                ' cooldown)',
            ];
        },
    },
    331: {
        name: 'Use Skill when enemy is killed with skill',
        wikiDesc: 'When you kill an enemy with a skill, uses SkillID at a % chance.',
        note: 'Trailing fields (`kill;1`) are undocumented.',
        describe({ params }) {
            const f = params.numbers();
            const skillId = f[0];
            const cooldownMs = f[1];
            const chance = f[2];
            if (isNaN(skillId) || isNaN(cooldownMs) || isNaN(chance)) {
                return null;
            }
            const out: Segment[] = [
                'When you kill an enemy with a skill, at a ', seg.pct(chance / 100), ' chance, uses ',
                seg.skill(skillId),
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    332: {
        name: 'Use skill on broken SA',
        wikiDesc: 'If the super armor of any skill other than your ultimate gets destroyed, uses ' +
            'SkillID at a % Chance.',
        describe({ params }) {
            const f = params.numbers();
            const skillId = f[0];
            const cooldownMs = f[1];
            const chance = f[2];
            if (isNaN(skillId) || isNaN(cooldownMs) || isNaN(chance)) {
                return null;
            }
            const out: Segment[] = [
                "When a non-ultimate skill's super armor is broken, at a ", seg.pct(chance / 100),
                ' chance, uses ', seg.skill(skillId),
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    335: {
        name: 'Next Set Action',
        wikiDesc: "When this state effect ends, applies NextSkillID's self effects at NextSkillLevel to " +
            "the owner. Only fires if the parent skill's duration type is Buff or Debuff.",
        describe({ params }) {
            const f = params.numbers();
            const skillId = f[0];
            const level = f[1];
            if (isNaN(skillId) || isNaN(level)) {
                return null;
            }
            return [
                'When this effect ends, applies ', seg.skill(skillId), "'s self effects at level ",
                seg.num(level), ' to the owner (only if the parent skill is Buff/Debuff type)',
            ];
        },
    },
    337: {
        name: 'Multi Action Blow',
        wikiDesc: "One-shot: your next cast of SkillID is replaced by that skill's configured " +
            'multi-action second skill (force-learned if missing), then this removes itself. Does ' +
            'nothing while a skill is already processing.',
        describe({ params }) {
            const skillId = params.nextNum();
            if (isNaN(skillId)) {
                return null;
            }
            return [
                'Your next cast of ', seg.skill(skillId),
                "'s configured multi-action second skill replaces it (force-learned if missing), then " +
                'this removes itself (no effect while a skill is already processing)',
            ];
        },
    },
    340: {
        name: 'Dragon Gem - Low Health Healing',
        wikiDesc: 'When you heal an ally within Distance whose HP is under HealthPercent%, Percent% ' +
            'chance the heal is multiplied by 1 + AddHealPercent/100. Skill heals only, items excluded.',
        describe({ params }) {
            const f = params.numbers();
            const cooldownMs = f[1];
            const percent = f[2];
            const distance = f[3];
            const healthPercent = f[4];
            const addHealPercent = f[5];
            if (isNaN(cooldownMs) || isNaN(percent) || isNaN(distance) || isNaN(healthPercent) ||
                isNaN(addHealPercent)) {
                return null;
            }
            const out: Segment[] = [
                'When healing an ally within ', seg.num(distance), ' range whose HP is under ',
                seg.pct(healthPercent / 100), ', at a ', seg.pct(percent / 100),
                ' chance, multiplies the heal by ', seg.pct(1 + addHealPercent / 100),
                ' (skill heals only, items excluded)',
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    341: {
        name: 'Use Skill after Successful Regular Attack',
        wikiDesc: 'After a successful regular attack, uses SkillID at a % Chance.',
        describe({ params }) {
            const f = params.fields();
            const skillId = ParamReader.num(f[0]);
            const cooldownSec = ParamReader.num(f[1]);
            const chance = ParamReader.num(f[2]);
            const scanDistance = ParamReader.num(f[3]);
            if (isNaN(skillId) || isNaN(cooldownSec) || isNaN(chance) || isNaN(scanDistance)) {
                return null;
            }
            const out: Segment[] = [
                'After a successful regular attack, at a ', seg.pct(chance / 100), ' chance, uses ',
                seg.skill(skillId), ' (targets within ', seg.num(scanDistance), ' range',
            ];
            if (cooldownSec > 0) {
                out.push(', ', seg.secs(cooldownSec * 1000), ' cooldown');
            }
            out.push(')');
            const allowIds = f.length > 4 ? ParamReader.numList(f.slice(4).join(','), ',') : [];
            if (allowIds.length) {
                out.push(', restricted to ',
                    seg.list(allowIds.map((id) => [seg.skill(id)] as Segment[])));
            }
            return out;
        },
    },
    343: {
        name: 'Dragon Gem - Use Tumble',
        wikiDesc: 'Applies a skill if you use an action with "Tumble" in its name.',
        describe({ params }) {
            const f = params.numbers();
            const skillId = f[0];
            const cooldownMs = f[1];
            const chance = f[2];
            if (isNaN(skillId) || isNaN(cooldownMs) || isNaN(chance)) {
                return null;
            }
            const out: Segment[] = [
                'Using an action with "Tumble" in its name, at a ', seg.pct(chance / 100), ' chance, uses ',
                seg.skill(skillId),
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    364: {
        name: 'Hit Add New',
        wikiDesc: 'Debuff on an enemy: every time the caster damages that target, the caster gains ' +
            "SkillID's self effects (created at level 1, no cast time or cooldown).",
        describe({ params }) {
            const skillId = params.nextNum();
            if (isNaN(skillId)) {
                return null;
            }
            return [
                'Debuff: every time the caster damages the holder, the caster gains ', seg.skill(skillId),
                "'s self effects (level 1, no cast time or cooldown)",
            ];
        },
    },
    365: {
        name: 'Element Charge Power',
        wikiDesc: "Using a skill of the matching element grants SkillID's self effects (level 1); " +
            'using a different element removes them. Non-elemental skills are ignored.',
        describe({ params }) {
            const f = params.numbers();
            const element = f[0];
            const skillId = f[1];
            if (isNaN(element) || isNaN(skillId)) {
                return null;
            }
            const ELEMENTS_364 = ['Fire', 'Ice', 'Light', 'Dark'];
            return [
                'Using a ', seg.en(element, ELEMENTS_364, 'unknown'), ' skill grants ', seg.skill(skillId),
                "'s self effects (level 1); using a different element removes them (non-elemental " +
                'skills ignored)',
            ];
        },
    },
    366: {
        name: "Don't Move Pow Charge",
        wikiDesc: 'After moving (MoveType 0) or standing still (MoveType 1) continuously for ' +
            "CheckTimeSec, applies SkillID's self effects (level 1), then repeats while active. Used " +
            'on Mana Surge.',
        describe({ params }) {
            const f = params.numbers();
            const checkTimeSec = f[0];
            const moveType = f[1];
            const skillId = f[2];
            if (isNaN(checkTimeSec) || isNaN(moveType) || isNaN(skillId)) {
                return null;
            }
            const MOVE_TYPES = ['moving', 'standing still'];
            return [
                'After ', seg.en(moveType, MOVE_TYPES, 'unknown'), ' continuously for ',
                seg.secs(checkTimeSec * 1000), ', applies ', seg.skill(skillId),
                "'s self effects (level 1), then repeats while active",
            ];
        },
    },
    367: {
        name: 'Hit Damage Heal Change',
        wikiDesc: 'Used on First Aid.',
        describe({ params }) {
            const multiplier = params.nextNum();
            if (isNaN(multiplier)) {
                return null;
            }
            return [
                'Multiplies damage-to-HP healing conversion by ', seg.pct(multiplier),
                ' (used by First Aid)',
            ];
        },
    },
    368: {
        name: 'Calc Blow Count',
        wikiDesc: 'Used on First Aid.',
        describe({ params }) {
            const f = params.numbers();
            const stateEffectId = f[0];
            const intervalMs = f[1];
            const count = f[2];
            const skillId = f[3];
            if (isNaN(stateEffectId) || isNaN(intervalMs) || isNaN(count) || isNaN(skillId)) {
                return null;
            }
            return [
                'Every ', seg.secs(intervalMs), ', counts stacks of ', seg.blow(stateEffectId), '; at ',
                seg.num(count), ' stacks, applies ', seg.skill(skillId), ' (used by First Aid)',
            ];
        },
    },
    382: {
        name: 'CheckBlowForParent',
        wikiDesc: '(On a summon) Every 1s: if the owner has state effect parentStateEffectID, plays ' +
            "subSkillID's action on the summon and applies its effects (level 1); removes them when " +
            'the owner loses it. Silver Hunter.',
        describe({ params }) {
            const f = params.numbers();
            const parentStateEffectId = f[0];
            const subSkillId = f[1];
            if (isNaN(parentStateEffectId) || isNaN(subSkillId)) {
                return null;
            }
            return [
                '(On a summon) Every 1s, if the owner has ', seg.blow(parentStateEffectId), ', plays ',
                seg.skill(subSkillId), "'s action on the summon and applies its effects (level 1); " +
                'removed when the owner loses it',
            ];
        },
    },
    402: {
        name: 'Chain Apply',
        wikiDesc: 'Applies skill effects in sequence: afflict the target with skillID1; when it ends, ' +
            'apply skillID2; when that ends, apply skillID3; and so on.',
        describe({ params }) {
            const f = params.fields();
            if (f.length < 2) {
                return null;
            }
            const groupId = ParamReader.num(f[0]);
            const chain = f.slice(1).map(ParamReader.num).filter((v) => !isNaN(v));
            if (isNaN(groupId) || !chain.length) {
                return null;
            }
            const items: Segment[][] = chain.map(
                (id) => (id === 0 ? [seg.val('(none)')] : [seg.skill(id)]));
            return [
                'Applies skill effects in sequence (group ', seg.id(groupId), '): ', seg.list(items),
            ];
        },
    },
    414: {
        name: 'Skill Hit Stack Up',
        wikiDesc: 'Stacking counter: re-applying with an identical param string adds HitStack to the ' +
            'counter and refreshes the duration (different param strings stack independently). At ' +
            "MaxStack, applies the listed skills' effects (level 1) to the holder and removes itself. " +
            'Stack count shows on the buff icon; StackType only groups the UI display. Cleansable.',
        describe({ params }) {
            const f = params.fields();
            const stackType = ParamReader.num(f[0]);
            const maxStack = ParamReader.num(f[1]);
            const hitStack = ParamReader.num(f[2]);
            const skillIds = f.slice(3).map(ParamReader.num).filter((v) => !isNaN(v));
            if (isNaN(stackType) || isNaN(maxStack) || isNaN(hitStack) || !skillIds.length) {
                return null;
            }
            return [
                'Adds ', seg.num(hitStack), ' stack(s) per re-application (refreshing duration; ' +
                'different param strings stack independently) up to ', seg.num(maxStack),
                '; at max stacks, applies ', seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])),
                "'s effects (level 1) and removes itself (cleansable)",
            ];
        },
    },
    415: {
        name: 'State Combine',
        wikiDesc: 'Fires once on apply then ends: if the holder currently has effects from ALL check ' +
            "skills, applies the success skills' self effects (level 1); otherwise the fail skills'. " +
            "RemoveFlag nonzero removes the check skills' effects either way. An empty check list " +
            'always succeeds.',
        describe({ params }) {
            const f = params.fields();
            if (f.length < 4) {
                return null;
            }
            const checkIds = ParamReader.numList(f[0], ',');
            const failIds = ParamReader.numList(f[1], ',');
            const successIds = ParamReader.numList(f[2], ',');
            const removeFlag = ParamReader.num(f[3]);
            if (isNaN(removeFlag)) {
                return null;
            }
            const idListItems = (fieldIndex: number): Segment[][] => {
                const fieldSpan = params.span(fieldIndex);
                const fieldReader = fieldSpan ? new ParamReader(f[fieldIndex], fieldSpan[0]) : null;
                const tokens = ParamReader.list(f[fieldIndex], ',');
                return sharedSpanListItems(tokens, fieldReader, ',', seg.skill);
            };
            const out: Segment[] = [];
            if (checkIds.length) {
                out.push(
                    'If the holder has effects from ',
                    seg.list(idListItems(0)), ', ');
            } else {
                out.push('(check list is empty, always succeeds) ');
            }
            out.push(
                'applies ',
                successIds.length
                    ? seg.list(idListItems(2))
                    : seg.val('(nothing)'),
                "'s self effects; otherwise applies ",
                failIds.length
                    ? seg.list(idListItems(1))
                    : seg.val('(nothing)'),
                "'s self effects");
            if (removeFlag) {
                out.push(" (removes the check skills' effects either way)");
            }
            return out;
        },
    },
    416: {
        name: 'Add Multiple Skill State',
        wikiDesc: 'Applies the state effects from other skills. Used for class buffs — the skills do ' +
            'not need to be autolearned, they are synthetic. rate (0.0-1.0) is a chance to apply, ' +
            'defaulting to 1.0 if omitted. overrideDuration can override the applied duration; ' +
            "defaults to -1 (use the skill's original duration).",
        describe({ params }) {
            const groups = params.fields(';');
            const items: Segment[][] = [];
            for (let i = 0; i < groups.length; ++i) {
                const group = groups[i];
                if (group === '') {
                    continue;
                }
                const parts = group.split(',').map((s) => s.trim());
                const skillId = ParamReader.num(parts[0]);
                if (isNaN(skillId)) {
                    continue;
                }
                const rate = parts.length > 1 ? ParamReader.num(parts[1]) : NaN;
                const overrideDuration = parts.length > 2 ? ParamReader.num(parts[2]) : NaN;
                const groupSpan = params.span(i, ';');
                const groupReader = groupSpan ? new ParamReader(group, groupSpan[0]) : null;
                const item: Segment[] = [
                    seg.skill(skillId, (groupReader && groupReader.span(0, ',')) || undefined),
                ];
                if (!isNaN(rate) && rate !== 1) {
                    item.push(
                        ' (', seg.pct(rate, 2, (groupReader && groupReader.span(1, ',')) || undefined),
                        ' chance)');
                }
                if (!isNaN(overrideDuration) && overrideDuration >= 0) {
                    item.push(
                        ' [', seg.secs(overrideDuration, (groupReader && groupReader.span(2, ',')) || undefined),
                        ' duration]');
                }
                items.push(item);
            }
            if (!items.length) {
                return null;
            }
            return ['Applies the state effects of ', seg.list(items)];
        },
    },
};
